import { Deserializable } from './deserializable';
import { Type } from '@angular/core';
import { WebSocketReceiveMessage } from '../web-sockets/messages/receive/web-socket-receive-message';
import { WebSocketMessageContextEvent } from '../web-sockets/contexts/web-socket-message-context-event';
import { WebSocketSyncJobUpdate } from '../web-sockets/messages/receive/web-socket-sync-job-update';
import { WebSocketMessageContextConnectionManager } from '../web-sockets/contexts/web-socket-message-context-connection-manager';
import { WebSocketConnectionEstablished } from '../web-sockets/messages/receive/web-socket-connection-established';
import { WebSocketPong } from '../web-sockets/messages/receive/web-socket-pong';
import { WebSocketHealthCheck } from '../web-sockets/messages/receive/web-socket-health-check';
import { WebSocketReceiveData } from '../web-sockets/messages/receive/web-socket-receive-data';
import { WebSocketMessageContextResponse } from '../web-sockets/contexts/web-socket-message-context-response';
import { WebSocketReceiveUpdate } from '../web-sockets/messages/receive/web-socket-receive-update';
import { WebSocketReceiveDelete } from '../web-sockets/messages/receive/web-socket-receive-delete';
import { WebSocketMessageType } from '../web-sockets/web-socket-message-type';
import { WebSocketReceiveError } from '../web-sockets/messages/receive/web-socket-receive-error';
import { WebSocketReceiveErrorWithNoRestfulFallback } from '../web-sockets/messages/receive/web-socket-receive-error-with-no-restful-fallback';
import { WebSocketMessageContextCommand } from '../web-sockets/contexts/web-socket-message-context-command';
import { WebSocketReceiveReloadCompany } from '../web-sockets/messages/receive/web-socket-receive-reload-company';
import { WebSocketReceiveReloadMenu } from '../web-sockets/messages/receive/web-socket-receive-reload-menu';
import { WebSocketReceiveReloadTemplate } from '../web-sockets/messages/receive/web-socket-receive-reload-template';
import { WebSocketReceiveReloadSection } from '../web-sockets/messages/receive/web-socket-receive-reload-section';
import { WebSocketReceiveReloadTemplateSection } from '../web-sockets/messages/receive/web-socket-receive-reload-template-section';
import { WebSocketReceiveReloadVariantBadge } from '../web-sockets/messages/receive/web-socket-receive-reload-variant-badge';
import { WebSocketAssetTranscodeStatusUpdate } from '../web-sockets/messages/receive/web-socket-asset-transcode-status-update';
import { WebSocketAssetUploaded } from '../web-sockets/messages/receive/web-socket-asset-uploaded';
import { WebSocketAssetDeleted } from '../web-sockets/messages/receive/web-socket-asset-deleted';
import { WebSocketReceiveBadgeIds } from '../web-sockets/messages/receive/web-socket-receive-badge-ids';
import { WebSocketMessageContextError } from '../web-sockets/contexts/web-socket-message-context-error';
import { WebSocketDuplicateAssetError } from '../web-sockets/messages/receive/web-socket-duplicate-asset-error';
import { WebSocketMessageContextDataUpdate } from '../web-sockets/contexts/web-socket-message-context-data-update';
import { WebSocketReceiveAssetUpdated } from '../web-sockets/messages/receive/web-socket-receive-asset-updated';
import { WebSocketReceiveReloadUser } from '../web-sockets/messages/receive/web-socket-receive-reload-user';
import { WebSocketReceiveReloadTemplateCollection } from '../web-sockets/messages/receive/web-socket-receive-reload-template-collection';
import { WebSocketReceiveReloadTagGroups } from '../web-sockets/messages/receive/web-socket-receive-reload-tag-groups';
import { WebSocketReceiveHighGuideCompanySignalCreated } from '../web-sockets/messages/receive/web-socket-receive-high-guide-company-signal-created';
import { WebSocketReceiveHighGuideProductSignalCreated } from '../web-sockets/messages/receive/web-socket-receive-high-guide-product-signal-created';
import { WebSocketReceiveHighGuideCompanySignalSnapshot } from '../web-sockets/messages/receive/web-socket-receive-high-guide-company-signal-snapshot';

export class DeserializeWebSocketMessageHelper {
  /**
   * If a message has explicit data types coming back, then the WebSocketMessage object needs to be extended and
   * the specific override for the payload property needs to be implemented (with its own deserialization logic).
   */
  static getPolymorphicMessageObject<T extends Deserializable>(message: any): T {
    const createPolymorphicObject = (ObjectType: Type<any>): T => {
      return Object.assign(Object.create(ObjectType.prototype), message);
    };
    if (message?.messageType === WebSocketMessageType.ErrorWithNoRestfulFallback) {
      return createPolymorphicObject(WebSocketReceiveErrorWithNoRestfulFallback);
    }
    if (message?.messageType === WebSocketMessageType.Error) {
      switch (message?.messageContext) {
        case WebSocketMessageContextError.DuplicateAssetExists:
          return createPolymorphicObject(WebSocketDuplicateAssetError);
      }
      return createPolymorphicObject(WebSocketReceiveError);
    }
    switch (message?.messageContext) {
      case WebSocketMessageContextConnectionManager.ConnectionEstablished:
        return createPolymorphicObject(WebSocketConnectionEstablished);
      case WebSocketMessageContextConnectionManager.Pong:
        return createPolymorphicObject(WebSocketPong);
      case WebSocketMessageContextConnectionManager.HealthCheck:
        return createPolymorphicObject(WebSocketHealthCheck);
      case WebSocketMessageContextDataUpdate.AssetUpdated:
        return createPolymorphicObject(WebSocketReceiveAssetUpdated);
      case WebSocketMessageContextDataUpdate.HighGuideCompanySignalSnapshot:
        return createPolymorphicObject(WebSocketReceiveHighGuideCompanySignalSnapshot);
      case WebSocketMessageContextEvent.AssetTranscodeStatusUpdate:
        return createPolymorphicObject(WebSocketAssetTranscodeStatusUpdate);
      case WebSocketMessageContextEvent.AssetUploaded:
        return createPolymorphicObject(WebSocketAssetUploaded);
      case WebSocketMessageContextEvent.AssetDeleted:
        return createPolymorphicObject(WebSocketAssetDeleted);
      case WebSocketMessageContextEvent.BadgeCreated:
      case WebSocketMessageContextEvent.BadgeDeleted:
        return createPolymorphicObject(WebSocketReceiveBadgeIds);
      case WebSocketMessageContextEvent.HighGuideCompanySignalCreated:
        return createPolymorphicObject(WebSocketReceiveHighGuideCompanySignalCreated);
      case WebSocketMessageContextEvent.HighGuideProductSignalCreated:
        return createPolymorphicObject(WebSocketReceiveHighGuideProductSignalCreated);
      case WebSocketMessageContextEvent.SyncJobUpdate:
        return createPolymorphicObject(WebSocketSyncJobUpdate);
      case WebSocketMessageContextCommand.ReloadCompany:
        return createPolymorphicObject(WebSocketReceiveReloadCompany);
      case WebSocketMessageContextCommand.ReloadMenu:
        return createPolymorphicObject(WebSocketReceiveReloadMenu);
      case WebSocketMessageContextCommand.ReloadSection:
        return createPolymorphicObject(WebSocketReceiveReloadSection);
      case WebSocketMessageContextCommand.ReloadTemplate:
        return createPolymorphicObject(WebSocketReceiveReloadTemplate);
      case WebSocketMessageContextCommand.ReloadTemplateSection:
        return createPolymorphicObject(WebSocketReceiveReloadTemplateSection);
      case WebSocketMessageContextCommand.ReloadVariantBadge:
        return createPolymorphicObject(WebSocketReceiveReloadVariantBadge);
      case WebSocketMessageContextCommand.ReloadUser:
        return createPolymorphicObject(WebSocketReceiveReloadUser);
      case WebSocketMessageContextCommand.ReloadTemplateCollection:
        return createPolymorphicObject(WebSocketReceiveReloadTemplateCollection);
      case WebSocketMessageContextCommand.ReloadTagGroups:
        return createPolymorphicObject(WebSocketReceiveReloadTagGroups);
      case WebSocketMessageContextResponse.RequestResponse:
        if (message?.payload?.messageContext?.includes('Update')) {
          return createPolymorphicObject(WebSocketReceiveUpdate);
        }
        // Prefix match, because action request types such as BulkDeleteAssets also contain 'Delete'
        if (message?.payload?.messageContext?.startsWith('WebsocketDeleteRequestType_')) {
          return createPolymorphicObject(WebSocketReceiveDelete);
        }
        return createPolymorphicObject(WebSocketReceiveData);
    }
    return createPolymorphicObject(WebSocketReceiveMessage);
  }
}
